import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { PlayIcon, StopCircleIcon } from 'lucide-react';
export function TourManagement() {
  const [activeTab, setActiveTab] = useState<'applications' | 'apply' | 'tracking' | 'history' | 'startTrip'>('applications');
  const [currentStep, setCurrentStep] = useState(0);
  // Apply Permit Form State
  const [applyForm, setApplyForm] = useState({
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    tourType: '',
    duration: '',
    groupSize: '',
    forestZone: '',
    purpose: '',
    guideSelection: '',
    vesselType: ''
  });
  // Start Trip Form State
  const [startTripForm, setStartTripForm] = useState({
    permitId: '',
    startLocation: '',
    startDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5)
  });
  // Data states
  const [applications, setApplications] = useState([{
    permitId: 'TOUR-2024-001',
    contactName: 'John Doe',
    tourType: 'Eco Tour',
    appliedDate: '2024-03-28',
    duration: '3 days',
    guideName: 'Mostafa Kamal',
    status: 'pending' as const
  }, {
    permitId: 'TOUR-2024-002',
    contactName: 'Jane Smith',
    tourType: 'Educational Tour',
    appliedDate: '2024-03-27',
    duration: '5 days',
    guideName: 'Rafiq Ahmed',
    status: 'approved' as const
  }, {
    permitId: 'TOUR-2024-003',
    contactName: 'Robert Johnson',
    tourType: 'Research Tour',
    appliedDate: '2024-03-26',
    duration: '7 days',
    guideName: 'Selina Akter',
    status: 'approved' as const
  }]);
  const [activeTrips, setActiveTrips] = useState([{
    contactName: 'Jane Smith',
    guideName: 'Rafiq Ahmed',
    vesselType: 'Launch',
    zone: 'Chandpai Range',
    entryTime: '2024-03-30 06:00',
    elapsed: '4h 30m'
  }, {
    contactName: 'Robert Johnson',
    guideName: 'Selina Akter',
    vesselType: 'Boat',
    zone: 'Sarankhola Range',
    entryTime: '2024-03-30 05:30',
    elapsed: '5h 00m'
  }]);
  const [tripHistory, setTripHistory] = useState([{
    tripId: 'TRIP-TOUR-001',
    contactName: 'Alice Williams',
    guideName: 'Habibur Rahman',
    vesselType: 'Ship',
    zone: 'Chandpai Range',
    entry: '2024-03-29 06:00',
    exit: '2024-03-29 18:30',
    duration: '12h 30m',
    status: 'approved' as const
  }]);
  const approvedPermits = applications.filter(p => p.status === 'approved');
  const selectedPermit = approvedPermits.find(p => p.permitId === startTripForm.permitId);
  const applicationColumns = [{
    key: 'permitId',
    label: 'Permit ID'
  }, {
    key: 'contactName',
    label: 'Contact Name'
  }, {
    key: 'tourType',
    label: 'Tour Type'
  }, {
    key: 'appliedDate',
    label: 'Applied Date'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'guideName',
    label: 'Guide'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2737" data-mp="2m84uj8xl9bx8" />
  }];
  const historyColumns = [{
    key: 'tripId',
    label: 'Trip ID'
  }, {
    key: 'contactName',
    label: 'Contact Name'
  }, {
    key: 'guideName',
    label: 'Guide'
  }, {
    key: 'vesselType',
    label: 'Vessel Type'
  }, {
    key: 'zone',
    label: 'Zone'
  }, {
    key: 'entry',
    label: 'Entry Time'
  }, {
    key: 'exit',
    label: 'Exit Time'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2738" data-mp="1pjxi95uik9vu" />
  }];
  const filters = [{
    key: 'tourType',
    label: 'Tour Type',
    options: ['Eco Tour', 'Educational Tour', 'Research Tour']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Pending', 'Approved', 'Rejected']
  }];
  const wizardSteps = [{
    title: 'Contact Information',
    description: 'Enter contact details'
  }, {
    title: 'Tour Details',
    description: 'Enter tour information'
  }, {
    title: 'Guide & Vessel',
    description: 'Select guide and vessel type'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-2739" data-mp="2wykn3gskozpt">
      <div data-id="element-2740" data-mp="2wzc5tr12n2yo">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2741" data-mp="31wcg6e4yervl">Tour Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2742" data-mp="31yggyn7k3arn">
          Manage tour permits and track trips
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2743" data-mp="105iura8abgfo">
        <div className="flex gap-4" data-id="element-2744" data-mp="1047qzaeua8ok">
          <button onClick={() => setActiveTab('applications')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'applications' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2745" data-mp="1a8o6u3sbjpsd">
            Tour Applications
          </button>
          <button onClick={() => setActiveTab('apply')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'apply' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2746" data-mp="1e9pynzjwozkb">
            Apply for Tour Permit
          </button>
          <button onClick={() => setActiveTab('tracking')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tracking' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2747" data-mp="21xr9xg8gyu3f">
            Trip Tracking ({activeTrips.length})
          </button>
          <button onClick={() => setActiveTab('history')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'history' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2748" data-mp="22qixm5c4vu04">
            Trip History
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'applications' && <DataTable columns={applicationColumns} data={applications} filters={filters} onDataChange={setApplications} searchPlaceholder="Search tour permits..." data-id="element-2749" data-mp="2lntlyuw8930s" />}

      {activeTab === 'apply' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!applyForm.contactName || !applyForm.tourType) {
        alert('Please fill in required fields.');
        return;
      }
      const newPermit = {
        permitId: `TOUR-2024-${String(applications.length + 1).padStart(3, '0')}`,
        contactName: applyForm.contactName,
        tourType: applyForm.tourType,
        appliedDate: new Date().toISOString().split('T')[0],
        duration: applyForm.duration ? `${applyForm.duration} days` : 'Not specified',
        guideName: applyForm.guideSelection || 'Not assigned',
        status: 'pending' as const
      };
      setApplications([newPermit, ...applications]);
      alert('Tour permit application submitted successfully!');
      setCurrentStep(0);
      setActiveTab('applications');
      // Reset form
      setApplyForm({
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        tourType: '',
        duration: '',
        groupSize: '',
        forestZone: '',
        purpose: '',
        guideSelection: '',
        vesselType: ''
      });
    }} data-id="element-2750" data-mp="30by5u270nkl5">
          {currentStep === 0 && <div className="space-y-4" data-id="element-2751" data-mp="uptjpk7tau03">
              <div data-id="element-2752" data-mp="ur4pfpktibtu">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2753" data-mp="uro9bbu13aai">
                  Contact Name *
                </label>
                <input type="text" required value={applyForm.contactName} onChange={e => setApplyForm({
            ...applyForm,
            contactName: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2754" data-mp="1mh973gh70krd" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2755" data-mp="6zzbjfyfttm6">
                <div data-id="element-2756" data-mp="71ttqmibr7mk">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2757" data-mp="6xwbs47hgm49">
                    Contact Phone *
                  </label>
                  <input type="tel" required value={applyForm.contactPhone} onChange={e => setApplyForm({
              ...applyForm,
              contactPhone: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2758" data-mp="3qnsuwilf499g" />
                </div>
                <div data-id="element-2759" data-mp="3lpivfvsnumi1">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2760" data-mp="3lpr9vs4flzvc">
                    Contact Email
                  </label>
                  <input type="email" value={applyForm.contactEmail} onChange={e => setApplyForm({
              ...applyForm,
              contactEmail: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2761" data-mp="2t6ve8rgsbrm8" />
                </div>
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-2762" data-mp="3d65prftpjcxk">
              <div data-id="element-2763" data-mp="3d7yv8rsl3r3z">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2764" data-mp="3d8idk9m5chij">
                  Tour Type *
                </label>
                <select value={applyForm.tourType} onChange={e => setApplyForm({
            ...applyForm,
            tourType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2765" data-mp="33gcksxo2lw2a">
                  <option value="" data-id="element-2766" data-mp="14zkcr40xonh3">Select Type</option>
                  <option value="Eco Tour" data-id="element-2767" data-mp="151530hg7tk95">Eco Tour</option>
                  <option value="Educational Tour" data-id="element-2768" data-mp="18z2szmht8adl">Educational Tour</option>
                  <option value="Research Tour" data-id="element-2769" data-mp="1dz7btj66xh6z">Research Tour</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2770" data-mp="1ixin7yc4vzrb">
                <div data-id="element-2771" data-mp="latel5mxeowh">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2772" data-mp="lbbgoiw9rxcb">
                    Requested Duration (Days) *
                  </label>
                  <input type="number" value={applyForm.duration} onChange={e => setApplyForm({
              ...applyForm,
              duration: e.target.value
            })} placeholder="e.g. 3" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2773" data-mp="q7tl1hnotto4" />
                </div>
                <div data-id="element-2774" data-mp="3rmrm1jo4xncq">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2775" data-mp="3rlgh3giye9k1">
                    Group Size *
                  </label>
                  <input type="number" value={applyForm.groupSize} onChange={e => setApplyForm({
              ...applyForm,
              groupSize: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2776" data-mp="9amlttrvwjr7" />
                </div>
              </div>
              <div data-id="element-2777" data-mp="11x7eqyo0d3wv">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2778" data-mp="11wo7cbvzhxx3">
                  Forest Zone/Area *
                </label>
                <select value={applyForm.forestZone} onChange={e => setApplyForm({
            ...applyForm,
            forestZone: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2779" data-mp="wxl0clwg1hmh">
                  <option value="" data-id="element-2780" data-mp="2aw8mx1ll80rs">Select Zone</option>
                  <option value="Chandpai Range" data-id="element-2781" data-mp="2ykrufewtc9it">Chandpai Range</option>
                  <option value="Sarankhola Range" data-id="element-2782" data-mp="32l26n9b77w64">Sarankhola Range</option>
                  <option value="Khulna Range" data-id="element-2783" data-mp="32n4w9s8fvnab">Khulna Range</option>
                  <option value="Satkhira Range" data-id="element-2784" data-mp="2otdx9dsc3lsu">Satkhira Range</option>
                </select>
              </div>
              <div data-id="element-2785" data-mp="2tqvy0gw76upr">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2786" data-mp="3hikvebnvm9dk">
                  Purpose
                </label>
                <textarea rows={3} value={applyForm.purpose} onChange={e => setApplyForm({
            ...applyForm,
            purpose: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2787" data-mp="3m9jcveklpdie"></textarea>
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-2788" data-mp="2gd6hum9s4lw6">
              <div data-id="element-2789" data-mp="2ccmpdw05i6gd">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2790" data-mp="2cc4kyc3c4lxd">
                  Guide Selection *
                </label>
                <select value={applyForm.guideSelection} onChange={e => setApplyForm({
            ...applyForm,
            guideSelection: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2791" data-mp="27jjqr9gio6tb">
                  <option value="" data-id="element-2792" data-mp="37l82i9l7tl0c">Select Guide</option>
                  <option value="Mostafa Kamal" data-id="element-2793" data-mp="37lzwjfz5zaoi">Mostafa Kamal (GD-001)</option>
                  <option value="Rafiq Ahmed" data-id="element-2794" data-mp="3cji2qylsrbst">Rafiq Ahmed (GD-002)</option>
                  <option value="Selina Akter" data-id="element-2795" data-mp="3gmo3jqq54arw">Selina Akter (GD-003)</option>
                  <option value="Habibur Rahman" data-id="element-2796" data-mp="3ll94xth4p0hx">
                    Habibur Rahman (GD-004)
                  </option>
                </select>
              </div>
              <div data-id="element-2797" data-mp="3qircbffhz7nt">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2798" data-mp="3qhzioa5gnk06">
                  Vessel Type *
                </label>
                <select value={applyForm.vesselType} onChange={e => setApplyForm({
            ...applyForm,
            vesselType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2799" data-mp="459ge89vbh93">
                  <option value="" data-id="element-2800" data-mp="258a7jayn72sv">Select Type</option>
                  <option value="Boat" data-id="element-2801" data-mp="2iydrfva1ukf5">Boat</option>
                  <option value="Launch" data-id="element-2802" data-mp="2izyivc12hn8s">Launch</option>
                  <option value="Ship" data-id="element-2803" data-mp="2e7x85ujjs4kv">Ship</option>
                  <option value="Trawler" data-id="element-2804" data-mp="2e6cfybkpgxq6">Trawler</option>
                </select>
              </div>
            </div>}

          {currentStep === 3 && <div className="space-y-6" data-id="element-2805" data-mp="2ny9w7wqkmfvr">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2806" data-mp="2nzkwhmkt55i4">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2807" data-mp="31w6s0qx7ai9d">
                  Contact Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2808" data-mp="2wyofkqjz5kwm">
                  <div data-id="element-2809" data-mp="17ifal58mqu3d">
                    <span className="text-gray-600" data-id="element-2810" data-mp="17k07v2mbwr4x">Name:</span>{' '}
                    <span className="font-medium" data-id="element-2811" data-mp="12knm6l771q3x">
                      {applyForm.contactName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2812" data-mp="3e9tq63pv5pu4">
                    <span className="text-gray-600" data-id="element-2813" data-mp="3j73mm3bo71rg">Phone:</span>{' '}
                    <span className="font-medium" data-id="element-2814" data-mp="3j8er65cxwdjm">
                      {applyForm.contactPhone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2815" data-mp="39ffz2di8i4v9">
                    <span className="text-gray-600" data-id="element-2816" data-mp="39eo331kq654g">Email:</span>{' '}
                    <span className="font-medium" data-id="element-2817" data-mp="2vg81tq5mt6tp">
                      {applyForm.contactEmail || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2818" data-mp="2mhvziqvekklf">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2819" data-mp="2qhd5dleqrjr4">
                  Tour Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2820" data-mp="kpzp5kkz1gs8">
                  <div data-id="element-2821" data-mp="oqjp0vwxiyi2">
                    <span className="text-gray-600" data-id="element-2822" data-mp="oqtfslpst9ot">Type:</span>{' '}
                    <span className="font-medium" data-id="element-2823" data-mp="38mkncyeamh0e">
                      {applyForm.tourType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2824" data-mp="33nzkssbkjl8s">
                    <span className="text-gray-600" data-id="element-2825" data-mp="33ptzhow8yh4f">Duration:</span>{' '}
                    <span className="font-medium" data-id="element-2826" data-mp="2yv6g24g3ocwp">
                      {applyForm.duration ? `${applyForm.duration} days` : 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2827" data-mp="2kwzw7s7zkdg1">
                    <span className="text-gray-600" data-id="element-2828" data-mp="2kwqbok5pmyhq">Zone:</span>{' '}
                    <span className="font-medium" data-id="element-2829" data-mp="2fxwzqw62nfjo">
                      {applyForm.forestZone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2830" data-mp="2b0n0kosvrpin">
                    <span className="text-gray-600" data-id="element-2831" data-mp="2b16kgb23cnzb">Group Size:</span>{' '}
                    <span className="font-medium" data-id="element-2832" data-mp="e5qw9tfatogk">
                      {applyForm.groupSize ? `${applyForm.groupSize} persons` : 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2833" data-mp="loqc5p3jwxv6">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2834" data-mp="qh1boqudqhn7">
                  Guide & Vessel
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2835" data-mp="vg4cfz3bu1pd">
                  <div data-id="element-2836" data-mp="3uuzltmrxapjm">
                    <span className="text-gray-600" data-id="element-2837" data-mp="3uvrh0whlyl9d">Guide:</span>{' '}
                    <span className="font-medium" data-id="element-2838" data-mp="2r73iml09h9a">
                      {applyForm.guideSelection || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2839" data-mp="7oyt39y6grab">
                    <span className="text-gray-600" data-id="element-2840" data-mp="7smlu6vst9nz">Vessel Type:</span>{' '}
                    <span className="font-medium" data-id="element-2841" data-mp="cqo97409bv59">
                      {applyForm.vesselType || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'tracking' && <div className="space-y-4" data-id="element-2842" data-mp="2zz3c5r1y6d4s">
          <div className="flex justify-end" data-id="element-2843" data-mp="34y6h7bhxgjcr">
            <button onClick={() => setActiveTab('startTrip')} className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-2844" data-mp="34wve7dwb3fmp">
              <PlayIcon size={16} data-id="element-2845" data-mp="3enq3kr4umvcd" />
              Start New Trip
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2846" data-mp="2h22580ou22y7">
            {activeTrips.map((trip, idx) => <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2847" data-mp="2m1ez35e4gaaa" data-mpi={idx}>
                <div className="flex items-center justify-between mb-3" data-id="element-2848" data-mp="2v0ijqdsxsxqw">
                  <h4 className="font-semibold text-gray-900" data-id="element-2849" data-mp="68pulfhawdei">
                    {trip.contactName}
                  </h4>
                  <div className="flex items-center gap-1" data-id="element-2850" data-mp="a99qygu1vczl">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-2851" data-mp="abvve7ln9cdl"></div>
                    <span className="text-xs text-green-600 font-medium" data-id="element-2852" data-mp="1utwpcetx4zan">
                      In Forest
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600" data-id="element-2853" data-mp="1ly754947iayu">
                  <div data-id="element-2854" data-mp="1h1gsjvrm1xi6">
                    <span className="font-medium" data-id="element-2855" data-mp="1h05pxz9wivsn">Guide:</span> {trip.guideName}
                  </div>
                  <div data-id="element-2856" data-mp="1c35ipl1bfmzy">
                    <span className="font-medium" data-id="element-2857" data-mp="1bz7yew304peh">Vessel Type:</span>{' '}
                    {trip.vesselType}
                  </div>
                  <div data-id="element-2858" data-mp="130e7nmccqt8u">
                    <span className="font-medium" data-id="element-2859" data-mp="131pa9iu29uyd">Zone:</span> {trip.zone}
                  </div>
                  <div data-id="element-2860" data-mp="y34i86oyqhl0">
                    <span className="font-medium" data-id="element-2861" data-mp="y3nsr2caa3p0">Entry:</span> {trip.entryTime}
                  </div>
                  <div data-id="element-2862" data-mp="2xcuiz1k08xkk">
                    <span className="font-medium" data-id="element-2863" data-mp="2sfuayl3lhkqt">Elapsed:</span> {trip.elapsed}
                  </div>
                </div>
                <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50" data-id="element-2864" data-mp="3bk78ux8yl2gp">
                  <StopCircleIcon size={16} data-id="element-2865" data-mp="3gd0hhw7jsuy2" />
                  End Trip
                </button>
              </div>)}
          </div>
        </div>}

      {activeTab === 'startTrip' && <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl mx-auto" data-id="element-2866" data-mp="3v8gdi6rov0x3">
          <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4" data-id="element-2867" data-mp="3v6ltqvs5leul">
            <div data-id="element-2868" data-mp="32b4o50y69y3">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-2869" data-mp="3laiovi5x323y">
                Start Tour Trip
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-2870" data-mp="3q8k946f2x7h0">
                Select an approved permit and provide trip details
              </p>
            </div>
            <div className="text-right" data-id="element-2871" data-mp="29xga8sndommf">
              <div className="text-sm text-gray-500" data-id="element-2872" data-mp="2euggb3k89pdj">
                Trip ID (Auto-generated)
              </div>
              <div className="font-mono font-bold text-lg text-[#1B5E20]" data-id="element-2873" data-mp="279m8h9madnlc">
                TRP-TOUR-
                {Math.floor(Math.random() * 10000).toString().padStart(4, '0')}
              </div>
            </div>
          </div>

          <form onSubmit={e => {
        e.preventDefault();
        alert('Trip started successfully! Status updated to Ongoing.');
        setActiveTab('tracking');
      }} className="space-y-6" data-id="element-2874" data-mp="2bbr42dwaouam">
            {/* Select Approved Permit */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-2875" data-mp="1xd17r0hauynb">
              <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-2876" data-mp="1sm183ngsq2h4">
                Select Approved Permit *
              </h3>
              <div data-id="element-2877" data-mp="14um4lrda9f4a">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2878" data-mp="14u3x1yl67eh2">
                  Permit
                </label>
                <select required value={startTripForm.permitId} onChange={e => setStartTripForm({
              ...startTripForm,
              permitId: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2879" data-mp="1dk2q8vkfkml5">
                  <option value="" data-id="element-2880" data-mp="1wa2g3ko3i5pm">Select an approved permit</option>
                  {approvedPermits.map(p => <option key={p.permitId} value={p.permitId} data-id="element-2881" data-mp="21b89lkpkxx07">
                      {p.permitId} — {p.contactName} ({p.tourType})
                    </option>)}
                </select>
              </div>

              {/* Permit Details Card */}
              {selectedPermit && <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-2882" data-mp="gtk39udu9hta">
                  <div className="flex items-center justify-between" data-id="element-2883" data-mp="gven15ddj3vs">
                    <h4 className="font-semibold text-gray-900" data-id="element-2884" data-mp="2u5ejgyyzzvuv">
                      Permit Details
                    </h4>
                    <StatusBadge status={selectedPermit.status} data-id="element-2885" data-mp="2pba7g1avkxmx" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2886" data-mp="2l9fata3w38fh">
                    <div data-id="element-2887" data-mp="2gdq3gklmon8m">
                      <span className="text-gray-600" data-id="element-2888" data-mp="2gac31hwiyo3t">Contact:</span>{' '}
                      <span className="font-medium" data-id="element-2889" data-mp="3d1e8c0woija8">
                        {selectedPermit.contactName}
                      </span>
                    </div>
                    <div data-id="element-2890" data-mp="341rbztit43o1">
                      <span className="text-gray-600" data-id="element-2891" data-mp="340yc9ci9mefe">Type:</span>{' '}
                      <span className="font-medium" data-id="element-2892" data-mp="2z36g26al865e">
                        {selectedPermit.tourType}
                      </span>
                    </div>
                    <div data-id="element-2893" data-mp="1so5mg791c01z">
                      <span className="text-gray-600" data-id="element-2894" data-mp="1nnrd8lvp6k2d">Duration:</span>{' '}
                      <span className="font-medium" data-id="element-2895" data-mp="1npubtuax1lip">
                        {selectedPermit.duration}
                      </span>
                    </div>
                    <div data-id="element-2896" data-mp="1u49k7016kduh">
                      <span className="text-gray-600" data-id="element-2897" data-mp="1u3hs3z6sky8y">Guide:</span>{' '}
                      <span className="font-medium" data-id="element-2898" data-mp="1g36y4na6r3jk">
                        {selectedPermit.guideName}
                      </span>
                    </div>
                    <div data-id="element-2899" data-mp="1l560btu6vuex">
                      <span className="text-gray-600" data-id="element-2900" data-mp="16dx5n6bfjiux">Applied Date:</span>{' '}
                      <span className="font-medium" data-id="element-2901" data-mp="1b55g85ohxa9o">
                        {selectedPermit.appliedDate}
                      </span>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Journey Details */}
            {selectedPermit && <div className="space-y-4" data-id="element-2902" data-mp="nfuplh0v0w99">
                <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-2903" data-mp="ng30x4hc3tic">
                  Journey Details
                </h3>
                <div data-id="element-2904" data-mp="8sw03rlkcbx6">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2905" data-mp="8s2nwb3u4u7n">
                    Start Location (Station) *
                  </label>
                  <select required value={startTripForm.startLocation} onChange={e => setStartTripForm({
              ...startTripForm,
              startLocation: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2906" data-mp="3vvol15y2gyo">
                    <option value="" data-id="element-2907" data-mp="11grvmszebxsd">Select Station</option>
                    <option value="chandpai" data-id="element-2908" data-mp="wjhu4eyqfvo6">Chandpai Station</option>
                    <option value="katka" data-id="element-2909" data-mp="1ahycj35yjam5">Katka Station</option>
                    <option value="dhangmari" data-id="element-2910" data-mp="1aihhzkef86ja">Dhangmari Station</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-2911" data-mp="16etb73xm5ez3">
                  <div data-id="element-2912" data-mp="2cusp65ztrnfm">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2913" data-mp="2cxompt7rga3j">
                      Start Date *
                    </label>
                    <input type="date" required value={startTripForm.startDate} onChange={e => setStartTripForm({
                ...startTripForm,
                startDate: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2914" data-mp="2mti7u0zjpbwx" />
                  </div>
                  <div data-id="element-2915" data-mp="27yc0naopr720">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2916" data-mp="1beutz8jqypb7">
                      Start Time *
                    </label>
                    <input type="time" required value={startTripForm.startTime} onChange={e => setStartTripForm({
                ...startTripForm,
                startTime: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2917" data-mp="1fen9k57k5vq2" />
                  </div>
                </div>
              </div>}

            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200" data-id="element-2918" data-mp="k5rx3dfk6xnq">
              <button type="button" onClick={() => setActiveTab('tracking')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium" data-id="element-2919" data-mp="f8jexcueutmh">
                Cancel
              </button>
              <button type="submit" disabled={!selectedPermit} className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2920" data-mp="19jrg8imwkrw">
                <PlayIcon size={18} data-id="element-2921" data-mp="3g8ya22atyzq9" />
                Start Trip
              </button>
            </div>
          </form>
        </div>}

      {activeTab === 'history' && <DataTable columns={historyColumns} data={tripHistory} onDataChange={setTripHistory} searchPlaceholder="Search trip history..." data-id="element-2922" data-mp="2nksojdbyd6pd" />}
    </div>;
}